// ============================================================================
// usePartnerDocuments — the partner-side document repository (AFB-0011).
// Reads/writes partner_documents (the structural mirror of employee_documents).
// Flat-fetch + optimistic upsert, matching the house style in
// use-onboarding-data.ts (no TanStack Query).
//
// URLs only — files live in the locked TCS-HR-Confidential Drive. The
// Direct_Deposit document_type is RLS-masked to HR + superuser; finance/AM
// never receive those rows, so they simply won't appear here for them.
//
// Writes are gated by RLS to hr + superuser (INSERT/UPDATE). Other roles get a
// graceful error the caller surfaces as a toast.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export interface PartnerDocument {
  document_id: string;
  provider_id: string | null;
  onboarding_id: string | null;
  document_type: string | null;
  document_name: string | null;
  status: string | null;
  drive_link: string | null;
  submitted_date: string | null;
  uploaded_by: string | null;
  reviewed_by: string | null;
  reviewed_date: string | null;
  expiration_date: string | null;
  notes: string | null;
}

const DOC_SELECT =
  'document_id, provider_id, onboarding_id, document_type, document_name, status, drive_link, submitted_date, uploaded_by, reviewed_by, reviewed_date, expiration_date, notes';

export interface UpsertDocInput {
  document_type: string;
  drive_link: string;
  document_name?: string | null;
  onboarding_id?: string | null;
  uploaded_by?: string | null;
  status?: string | null;
  expiration_date?: string | null;
}

export interface UsePartnerDocumentsResult {
  documents: PartnerDocument[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  // Upsert by (provider_id, document_type): updates the existing row of that
  // type if present, else inserts a new one. Returns nothing; rolls forward by
  // refetching. Throws on RLS denial so the caller can surface it.
  upsertDocument: (input: UpsertDocInput) => Promise<void>;
}

export function usePartnerDocuments(
  providerId: string | null,
): UsePartnerDocumentsResult {
  const [documents, setDocuments] = useState<PartnerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!providerId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: e } = await supabase
          .from('partner_documents')
          .select(DOC_SELECT)
          .eq('provider_id', providerId)
          .order('document_type', { ascending: true });
        if (cancelled) return;
        if (e) throw e;
        setDocuments((data ?? []) as PartnerDocument[]);
      } catch (err) {
        if (cancelled) return;
        console.error('[partner-documents] load failed', err);
        setError(
          errorMessage(err, 'Failed to load documents.'),
        );
        setDocuments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const upsertDocument = useCallback(
    async (input: UpsertDocInput) => {
      if (!providerId) throw new Error('No provider selected.');
      const existing = documents.find(
        (d) => d.document_type === input.document_type,
      );
      const today = new Date().toISOString().slice(0, 10);
      if (existing) {
        const { error: e } = await supabase
          .from('partner_documents')
          .update({
            drive_link: input.drive_link,
            document_name: input.document_name ?? existing.document_name,
            status: input.status ?? 'Received',
            submitted_date: existing.submitted_date ?? today,
            uploaded_by: input.uploaded_by ?? existing.uploaded_by,
            expiration_date:
              input.expiration_date ?? existing.expiration_date ?? null,
          })
          .eq('document_id', existing.document_id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase.from('partner_documents').insert({
          provider_id: providerId,
          onboarding_id: input.onboarding_id ?? null,
          document_type: input.document_type,
          document_name: input.document_name ?? input.document_type,
          status: input.status ?? 'Received',
          drive_link: input.drive_link,
          submitted_date: today,
          uploaded_by: input.uploaded_by ?? null,
          expiration_date: input.expiration_date ?? null,
        });
        if (e) throw e;
      }
      setTick((n) => n + 1);
    },
    [providerId, documents],
  );

  return { documents, loading, error, refetch, upsertDocument };
}
